/* Import Estilo */
import './index.scss';

/* Import Bibliotecas */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

/* Import Contextos */
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';

/* Import Componentes */
import Cabecalho from '../../components/Cabecalho';
import Rodape from '../../components/Rodape';

/* Import Requisição API */
import api from '../../api';

/* ============================================================
   Helpers
   ============================================================ */

// Exibe as estrelas de 1 a 5 com base na nota
function EstrellasDisplay({ nota, size = 22 }) {
    return (
        <div className="estrelas-display">
            {[1, 2, 3, 4, 5].map(i => (
                <svg
                    key={i}
                    width={size}
                    height={size}
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                    className={`estrela ${i <= nota ? 'preenchida' : 'vazia'}`}
                >
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
            ))}
        </div>
    );
}

// Avatar circular com fallback na inicial do nome
function Avatar({ foto, nome }) {
    const inicial = nome ? nome.charAt(0).toUpperCase() : '?';
    return foto ? (
        <img src={foto} alt={nome} className="avatar-foto" />
    ) : (
        <div className="avatar-inicial">{inicial}</div>
    );
}

// Label amigável para o nível de acesso
function labelNivel(nivel) {
    if (nivel === 'Atacadista') return 'Atacadista';
    if (nivel === 'Varejista') return 'Varejista';
    return 'Usuário';
}

/* ============================================================
   Componente principal
   ============================================================ */

export default function AvaliacoesSite() {
    const navigate = useNavigate();
    const { userId, nivelAcesso } = useAuth();
    const { showToast } = useToast();
    const isAdmin = nivelAcesso === 'Administrador';

    const [avaliacoes, setAvaliacoes] = useState([]);
    const [resumo, setResumo] = useState({ media: 0, total: 0 });
    const [userReacoes, setUserReacoes] = useState({}); // { id_avaliacao: 'like'|'dislike' }
    const [loading, setLoading] = useState(true);
    const [confirmDelete, setConfirmDelete] = useState(null); // id para confirmar exclusão
    const [podeAvaliar, setPodeAvaliar] = useState(null);

    /* Verifica se o usuário já fez algum pedido */
    useEffect(() => {
        if (!userId) return;
        api.get('/avaliacoes-site/pode-avaliar')
            .then(res => setPodeAvaliar(res.data.podeAvaliar))
            .catch(() => setPodeAvaliar(false));
    }, [userId]);

    /* Busca avaliações e resumo */
    const fetchAvaliacoes = useCallback(async () => {
        try {
            const endpoint = isAdmin ? '/avaliacoes-site/admin' : '/avaliacoes-site';
            const [resAv, resResumo] = await Promise.all([
                api.get(endpoint),
                api.get('/avaliacoes-site/resumo'),
            ]);
            if (resAv.data.success) setAvaliacoes(resAv.data.avaliacoes);
            if (resResumo.data.success) setResumo(resResumo.data);
        } catch (e) {
            showToast('Erro ao carregar avaliações.', 'error');
        } finally {
            setLoading(false);
        }
    }, [isAdmin]);

    /* Busca as reações do usuário logado */
    const fetchMinhasReacoes = useCallback(async () => {
        if (!userId) return;
        try {
            const res = await api.get('/avaliacoes-site/minhas-reacoes');
            if (res.data.success) setUserReacoes(res.data.reacoes);
        } catch {}
    }, [userId]);

    useEffect(() => {
        fetchAvaliacoes();
    }, [fetchAvaliacoes]);

    useEffect(() => {
        fetchMinhasReacoes();
    }, [fetchMinhasReacoes]);

    /* Like / Dislike */
    const handleReacao = async (id, tipo) => {
        if (!userId) {
            navigate('/login');
            return;
        }
        try {
            const res = await api.post(`/avaliacoes-site/${id}/reacao`, { tipo });
            if (res.data.success) {
                const acao = res.data.acao; // 'registrada' | 'alterada' | 'removida'
                setUserReacoes(prev => {
                    const novo = { ...prev };
                    if (acao === 'removida') {
                        delete novo[id];
                    } else {
                        novo[id] = tipo;
                    }
                    return novo;
                });
                // Atualiza contadores localmente
                setAvaliacoes(prev => prev.map(av => {
                    if (av.id !== id) return av;
                    const tipoAnterior = userReacoes[id];
                    let likes = Number(av.likes);
                    let dislikes = Number(av.dislikes);
                    if (tipoAnterior === 'like') likes--;
                    if (tipoAnterior === 'dislike') dislikes--;
                    if (acao !== 'removida') {
                        if (tipo === 'like') likes++;
                        if (tipo === 'dislike') dislikes++;
                    }
                    return { ...av, likes: Math.max(0, likes), dislikes: Math.max(0, dislikes) };
                }));
            }
        } catch {
            showToast('Erro ao registrar reação.', 'error');
        }
    };

    /* Ocultar / Mostrar (admin) */
    const handleOcultar = async (id) => {
        try {
            const res = await api.patch(`/avaliacoes-site/${id}/ocultar`);
            if (res.data.success) {
                setAvaliacoes(prev => prev.map(av =>
                    av.id === id ? { ...av, oculto: av.oculto ? 0 : 1 } : av
                ));
                showToast('Visibilidade alterada.', 'success');
            }
        } catch {
            showToast('Erro ao alterar visibilidade.', 'error');
        }
    };

    /* Excluir (admin) */
    const handleExcluir = async (id) => {
        try {
            const res = await api.delete(`/avaliacoes-site/${id}`);
            if (res.data.success) {
                setAvaliacoes(prev => prev.filter(av => av.id !== id));
                showToast('Avaliação excluída.', 'success');
            }
        } catch {
            showToast('Erro ao excluir avaliação.', 'error');
        } finally {
            setConfirmDelete(null);
        }
    };

    /* Renderiza estrelas da média no topo */
    const mediaArredondada = Math.round(resumo.media);

    return (
        <div className="avaliacoes-site-page">
            <Cabecalho />

            <main className="avaliacoes-site-main">
                {/* Cabeçalho resumo */}
                <section className="resumo-section">
                    <h1 className="resumo-titulo">Avaliações dos Clientes</h1>
                    <div className="resumo-estrelas">
                        <EstrellasDisplay nota={mediaArredondada} size={32} />
                        <span className="resumo-media">{resumo.media > 0 ? resumo.media.toFixed(1) : '0.0'}</span>
                        <span className="resumo-total">({resumo.total} {resumo.total === 1 ? 'avaliação' : 'avaliações'})</span>
                    </div>
                    {userId && podeAvaliar && (
                        <button
                            className="btn-escrever"
                            onClick={() => navigate('/escrever-avaliacao-site')}
                        >
                            Escrever uma avaliação
                        </button>
                    )}
                    {userId && podeAvaliar === false && (
                        <p className="aviso-necessario-pedido">Faça um pedido para poder avaliar a loja.</p>
                    )}
                    {!userId && (
                        <button
                            className="btn-escrever btn-escrever--login"
                            onClick={() => navigate('/login')}
                        >
                            Faça login para avaliar
                        </button>
                    )}
                </section>

                {/* Lista de avaliações */}
                <section className="lista-avaliacoes">
                    {loading && (
                        <p className="lista-loading">Carregando avaliações...</p>
                    )}

                    {!loading && avaliacoes.length === 0 && (
                        <p className="lista-vazia">Nenhuma avaliação disponível ainda. Seja o primeiro a avaliar!</p>
                    )}

                    {!loading && avaliacoes.map(av => (
                        <div
                            key={av.id}
                            className={`card-avaliacao ${av.oculto ? 'card-avaliacao--oculto' : ''}`}
                        >
                            {/* Cabeçalho do card — ações admin */}
                            {isAdmin && (
                                <div className="card-admin-actions">
                                    {av.oculto ? (
                                        <span className="badge-oculto">Oculto</span>
                                    ) : null}
                                    <button
                                        className="btn-admin btn-admin--ocultar"
                                        onClick={() => handleOcultar(av.id)}
                                        title={av.oculto ? 'Mostrar avaliação' : 'Ocultar avaliação'}
                                    >
                                        {av.oculto ? 'Mostrar' : 'Ocultar'}
                                    </button>
                                    <button
                                        className="btn-admin btn-admin--excluir"
                                        onClick={() => setConfirmDelete(av.id)}
                                    >
                                        Excluir
                                    </button>
                                </div>
                            )}

                            {/* Corpo do card */}
                            <div className="card-corpo">
                                {/* Foto à esquerda */}
                                <div className="card-avatar">
                                    <Avatar foto={av.foto_perfil} nome={av.nome} />
                                </div>

                                {/* Conteúdo à direita */}
                                <div className="card-conteudo">
                                    {/* Dois andares de texto */}
                                    <div className="card-usuario">
                                        <span className="card-nome">{av.nome}</span>
                                        <span className="card-nivel">{labelNivel(av.nivel_acesso)}</span>
                                    </div>

                                    {/* Estrelas e data */}
                                    <div className="card-nota-row">
                                        <EstrellasDisplay nota={av.nota} size={18} />
                                        <span className="card-data">
                                            {new Date(av.criado_em).toLocaleDateString('pt-BR', {
                                                day: '2-digit', month: '2-digit', year: 'numeric'
                                            })}
                                        </span>
                                    </div>

                                    {/* Comentário */}
                                    {av.comentario && (
                                        <p className="card-comentario">{av.comentario}</p>
                                    )}

                                    {/* Reações */}
                                    <div className="card-reacoes">
                                        <button
                                            className={`btn-reacao btn-like ${userReacoes[av.id] === 'like' ? 'ativo' : ''}`}
                                            onClick={() => handleReacao(av.id, 'like')}
                                            title="Curtir"
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z" />
                                                <path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
                                            </svg>
                                            <span>{av.likes}</span>
                                        </button>
                                        <button
                                            className={`btn-reacao btn-dislike ${userReacoes[av.id] === 'dislike' ? 'ativo' : ''}`}
                                            onClick={() => handleReacao(av.id, 'dislike')}
                                            title="Não curtir"
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3H10z" />
                                                <path d="M17 2h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17" />
                                            </svg>
                                            <span>{av.dislikes}</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </section>
            </main>

            {/* Modal de confirmação de exclusão */}
            {confirmDelete && (
                <div className="modal-overlay" onClick={() => setConfirmDelete(null)}>
                    <div className="modal-confirm" onClick={e => e.stopPropagation()}>
                        <h3>Confirmar exclusão</h3>
                        <p>Tem certeza que deseja excluir esta avaliação permanentemente?</p>
                        <div className="modal-actions">
                            <button className="btn-modal btn-modal--cancelar" onClick={() => setConfirmDelete(null)}>
                                Cancelar
                            </button>
                            <button className="btn-modal btn-modal--confirmar" onClick={() => handleExcluir(confirmDelete)}>
                                Excluir
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <Rodape />
        </div>
    );
}
